import type { PostEmbedView } from '../components/PostMedia'

/**
 * Bluesky moderation/self-label values that mark a post as adult.
 *
 * On Bluesky `nudity` covers BOTH non-sexual (art / beach pics) AND
 * sexual nudity, and many users only label the latter. `underwear`
 * is suggestive. `graphic-media` is gore. We keep all of them so
 * the gallery's "NSFW" / "adult" view is generous — most adult
 * content on Bluesky isn't formally labelled at all (see
 * ADULT_TAGS for the hashtag-based fallback).
 */
export const ADULT_LABELS: ReadonlySet<string> = new Set([
  'porn',
  'sexual',
  'sexual-content',
  'sexually-explicit',
  'sexual-figurative',
  'adult',
  'nsfw',
  'nudity',
  'underwear',
  'graphic-media',
])

/**
 * Hashtags (case-insensitive, no `#`) that flag a post as adult or
 * adjacent niche-NSFW. Generous list because most Bluesky adult
 * content rides on hashtags rather than formal labels.
 *
 * Includes the operator's specific asks (anime, hentai, women,
 * black-and-white) since this is a personal cache tool — adjust to
 * taste, but err on the side of catching more.
 */
export const ADULT_TAGS: ReadonlySet<string> = new Set([
  // generic NSFW signal words
  'nsfw',
  '18',
  '18plus',
  '18only',
  'r18',
  'r-18',
  'rule34',
  'r34',
  'lewd',
  'lewds',
  'thirst',
  'thirsty',
  'horny',
  'kinky',
  'kink',
  'fetish',
  'bdsm',
  'adult',
  'porn',
  'sex',
  'sexy',
  'sexual',
  'erotic',
  'erotica',
  // bodies / acts
  'naked',
  'nude',
  'nudes',
  'nudeart',
  'topless',
  'cum',
  'bj',
  'oral',
  'blowjob',
  'anal',
  'breeding',
  'creampie',
  'pussy',
  'cock',
  'dick',
  'tits',
  'titty',
  'titties',
  'boobs',
  'boobies',
  'breasts',
  'ass',
  'thighs',
  'thigh',
  'butt',
  'buttocks',
  'lingerie',
  // labels users self-apply
  'milf',
  'dilf',
  'twink',
  'femboy',
  'futanari',
  'futa',
  'bimbo',
  'sissy',
  'trans',
  'trap',
  // anime / manga niches
  'hentai',
  'ahegao',
  'ecchi',
  'doujin',
  'yaoi',
  'yuri',
  'anime',
  'manga',
  'waifu',
  'husbando',
  // photo niches the operator named
  'women',
  'woman',
  'ladies',
  'female',
  'femalegaze',
  'cosplay',
  'cosplaygirl',
  'lewdcosplay',
  'blackandwhite',
  'bnw',
  'bw',
  // explicit umbrella
  'xxx',
  'onlyfans',
  'of',
])

const HASHTAG_RE = /(?:^|\s|[(\[])#([\p{L}\p{N}_-]{2,40})/gu

export interface MediaThumb {
  /** Display URL for a tile (thumb if present, fullsize otherwise). */
  src: string
  /** Image alt text from the embed, when set by the author. */
  alt?: string
  /** True for video poster thumbnails — useful if a caller wants to
   *  badge the tile differently. */
  isVideo?: boolean
  /** Video HLS playlist URL — present when isVideo. Native &lt;video&gt;
   *  on Safari plays this directly; Chrome/Firefox need hls.js. */
  playlistUrl?: string
  /** Original full-size image URL — for animated GIFs the AppView
   *  serves the actual GIF here while `thumb` is a static JPEG. */
  fullsize?: string
  /** Author-declared display aspect ratio from the embed. For video this
   *  is the source of truth: some bsky-transcoded streams carry a bogus
   *  non-square pixel aspect ratio (SAR) that squishes the frame, so the
   *  player forces this ratio instead of trusting the decoded dimensions. */
  aspectRatio?: { width: number; height: number }
}

function readAspectRatio(v: unknown): { width: number; height: number } | undefined {
  const ar = v as { width?: unknown; height?: unknown } | undefined
  if (ar && typeof ar.width === 'number' && typeof ar.height === 'number' && ar.width > 0 && ar.height > 0) {
    return { width: ar.width, height: ar.height }
  }
  return undefined
}

/**
 * Walk an embed and return EVERY image / video thumbnail / external
 * thumb in it. Multi-image posts (`app.bsky.embed.images` with N
 * entries), recordWithMedia variants, and quote-post nested embeds
 * all fan out into the returned list. Empty array if no media.
 *
 * Pass `authorDid` to enable fallback CDN URL construction from AT
 * Protocol blob-ref format (images[].image.ref.$link) — needed when
 * a post embed was cached in PDS record format rather than AppView
 * #view format.
 */
export function allImagesOf(embed: PostEmbedView | undefined, authorDid?: string): MediaThumb[] {
  if (!embed) return []
  const out: MediaThumb[] = []
  walk(embed as Record<string, unknown>, out, authorDid)
  return out
}

function blobCdnUrl(did: string, cid: string, size: 'feed_thumbnail' | 'feed_fullsize', mime?: string): string {
  const ext = (mime ?? '').includes('png') ? 'png' : (mime ?? '').includes('webp') ? 'webp' : 'jpeg'
  return `https://cdn.bsky.app/img/${size}/plain/${did}/${cid}@${ext}`
}

function walk(shape: Record<string, unknown>, out: MediaThumb[], authorDid?: string): void {
  // 1. app.bsky.embed.images — multi-image posts.
  //    Handles both AppView format (thumb/fullsize CDN URLs) and AT
  //    Protocol record format (image.ref.$link blob CIDs).
  const images = (shape.images as {
    thumb?: string
    fullsize?: string
    alt?: string
    image?: { ref?: { '$link'?: string }; mimeType?: string }
    aspectRatio?: unknown
  }[] | undefined) ?? []
  for (const im of images) {
    const src = im.thumb ?? im.fullsize
    if (src) {
      out.push({ src, alt: im.alt, fullsize: im.fullsize })
    } else if (authorDid) {
      const cid = im.image?.ref?.['$link']
      if (cid) {
        out.push({
          src: blobCdnUrl(authorDid, cid, 'feed_thumbnail', im.image?.mimeType),
          alt: im.alt,
          fullsize: blobCdnUrl(authorDid, cid, 'feed_fullsize', im.image?.mimeType),
        })
      }
    }
  }
  // 1b. app.bsky.embed.gallery#view — new multi-media type (up to 9 items).
  //     Items use `thumbnail`/`fullsize` (not `thumb`). Also handles AT
  //     Protocol record format (item.image.ref.$link) as fallback.
  const items = (shape.items as {
    thumbnail?: string
    fullsize?: string
    alt?: string
    image?: { ref?: { '$link'?: string }; mimeType?: string }
    aspectRatio?: unknown
  }[] | undefined) ?? []
  for (const it of items) {
    const src = it.thumbnail ?? it.fullsize
    if (src) {
      out.push({ src, alt: it.alt, fullsize: it.fullsize })
    } else if (authorDid) {
      const cid = it.image?.ref?.['$link']
      if (cid) {
        out.push({
          src: blobCdnUrl(authorDid, cid, 'feed_thumbnail', it.image?.mimeType),
          alt: it.alt,
          fullsize: blobCdnUrl(authorDid, cid, 'feed_fullsize', it.image?.mimeType),
        })
      }
    }
  }
  // 1c. AppView may wrap images under a nested `view` key.
  const view = shape.view as Record<string, unknown> | undefined
  if (view && !images.length && !items.length) walk(view, out, authorDid)
  // 2. Video poster + playlist URL for in-tile playback.
  const video = shape as { thumbnail?: string; playlist?: string; alt?: string; aspectRatio?: unknown }
  if (typeof video.thumbnail === 'string' && video.thumbnail) {
    out.push({
      src: video.thumbnail,
      alt: video.alt,
      isVideo: true,
      playlistUrl: typeof video.playlist === 'string' ? video.playlist : undefined,
      aspectRatio: readAspectRatio(video.aspectRatio),
    })
  } else if (authorDid) {
    // Record-format app.bsky.embed.video (blob ref, no AppView URLs) —
    // video.bsky.app addresses streams by did+cid, so we can construct
    // both the poster and the playlist just like blobCdnUrl does for
    // images. Without this, cached record-format videos have no
    // playlistUrl and the lightbox can only show the poster.
    const t = typeof shape.$type === 'string' ? shape.$type : ''
    const vcid = (shape.video as { ref?: { '$link'?: string } } | undefined)?.ref?.['$link']
    if (t.startsWith('app.bsky.embed.video') && vcid) {
      const base = `https://video.bsky.app/watch/${encodeURIComponent(authorDid)}/${vcid}`
      out.push({
        src: `${base}/thumbnail.jpg`,
        alt: video.alt,
        isVideo: true,
        playlistUrl: `${base}/playlist.m3u8`,
      })
    }
  }
  // 3. External link card thumb. Tenor / Giphy GIFs land here — the
  //    `uri` is the actual animated media URL (.gif / .mp4 / .webm),
  //    `thumb` is a static jpg preview. Expose the playable uri as
  //    `fullsize` so the lightbox can animate it.
  const external = shape.external as
    | { thumb?: string; uri?: string; title?: string }
    | undefined
  if (external?.thumb) {
    const playable =
      typeof external.uri === 'string' && /\.(gif|mp4|webm)(\?|$)/i.test(external.uri)
        ? external.uri
        : undefined
    out.push({ src: external.thumb, alt: external.title, fullsize: playable })
  }
  // 4. recordWithMedia → media branch.
  const media = shape.media as Record<string, unknown> | undefined
  if (media) walk(media, out, authorDid)
  // 5. Quote-post — recurse into the quoted record's embeds.
  const record = shape.record as
    | {
        embeds?: Record<string, unknown>[]
        embed?: Record<string, unknown>
      }
    | undefined
  if (record) {
    if (Array.isArray(record.embeds)) {
      for (const re of record.embeds) walk(re, out, authorDid)
    }
    if (record.embed) walk(record.embed, out, authorDid)
  }
}

/**
 * Convenience: first image only — preserved for callers that just
 * need a representative tile (e.g. a single thumbnail in a list view).
 */
export function firstImageOf(embed: PostEmbedView | undefined): string | null {
  const all = allImagesOf(embed)
  return all.length > 0 ? all[0].src : null
}

/** True when ANY hashtag in `text` matches the adult tag set. Used
 *  by isAdultPost when a post has no formal label but lives in an
 *  adult niche. */
export function hasAdultTag(text: string | undefined | null): boolean {
  if (!text) return false
  const re = new RegExp(HASHTAG_RE.source, 'giu')
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) != null) {
    if (ADULT_TAGS.has(m[1].toLowerCase())) return true
  }
  return false
}

/** True when the post has a moderation label, self-label, OR
 *  adult-adjacent hashtag in its text. Mirrors the activity
 *  classifier so gallery filters match across the app. Bluesky
 *  often doesn't formally label adult content, so the hashtag
 *  fallback catches what labels miss. */
export function isAdultPost(p: {
  record?: {
    labels?: { values?: { val: string }[] }
    text?: string
  }
  labels?: { val: string }[]
  text?: string
}): boolean {
  for (const l of p.labels ?? []) {
    if (ADULT_LABELS.has(l.val)) return true
  }
  for (const v of p.record?.labels?.values ?? []) {
    if (ADULT_LABELS.has(v.val)) return true
  }
  if (hasAdultTag(p.record?.text)) return true
  if (hasAdultTag(p.text)) return true
  return false
}
